import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DeliveryDestinationNote } from 'customers';
import { NewCustomerAddress } from '../../../interfaces/new-customer.model';
import { NewCustomerDelivery } from '../../../interfaces/new-customer-requests.model';
import { NewCustomerService } from '../../../services/new-customer.service';
import { addressLocation, deliveryFromAddress } from '../../../utils/new-customer.utils';
import { NewCustomerDeliveryNotes } from '../new-customer-delivery-notes/new-customer-delivery-notes';
import { NewCustomerMapDialog } from '../new-customer-map-dialog/new-customer-map-dialog';

// Destino di consegna fra le sedi dell'azienda, oppure un nuovo destino scritto a mano
@Component({
  selector: 'ord-new-customer-deliveries',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, MatRadioModule, NewCustomerDeliveryNotes, ReactiveFormsModule],
  templateUrl: './new-customer-deliveries.html',
  styleUrl: './new-customer-deliveries.scss',
})
export class NewCustomerDeliveries implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchControl = new FormControl('', { nonNullable: true });
  private readonly searchText = signal('');
  readonly selectedId = signal<string | null>(null);

  // Le note scritte restano sulla sede anche cambiando scelta: vale quella scelta a «Prosegui»
  private readonly deliveries = new Map<string, NewCustomerDelivery>();

  readonly addresses = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    return this.flow.addresses().filter(address =>
      [address.tipoSede, address.indirizzo, address.cap, address.comune, address.provincia].join(' ').toLowerCase().includes(search));
  });

  ngOnInit(): void {
    const delivery = this.flow.delivery();
    if (delivery?.branchId) {
      this.selectedId.set(delivery.branchId);
      this.deliveries.set(delivery.branchId, delivery);
    }

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.searchText.set(value));
  }

  delivery(address: NewCustomerAddress): NewCustomerDelivery {
    return this.deliveries.get(address.branchId) ?? deliveryFromAddress(address);
  }

  isSelected(address: NewCustomerAddress): boolean {
    return this.selectedId() === address.branchId;
  }

  select(address: NewCustomerAddress): void {
    this.deliveries.set(address.branchId, this.delivery(address));
    this.selectedId.set(address.branchId);
  }

  setRiferimento(address: NewCustomerAddress, riferimento: string): void {
    this.deliveries.set(address.branchId, { ...this.delivery(address), riferimento });
  }

  setNota(address: NewCustomerAddress, nota: DeliveryDestinationNote): void {
    this.deliveries.set(address.branchId, { ...this.delivery(address), nota });
  }

  location(address: NewCustomerAddress): string {
    return addressLocation(address.cap, address.comune, address.provincia);
  }

  openMap(event: Event, address: NewCustomerAddress): void {
    event.stopPropagation();
    this.dialog.open<NewCustomerMapDialog, NewCustomerAddress>(NewCustomerMapDialog, { data: address, maxWidth: '95vw' });
  }

  continue(): void {
    const delivery = this.deliveries.get(this.selectedId() ?? '');
    if (this.flow.loading()) {
      return;
    }

    this.flow.setDelivery(delivery ?? null);
    this.flow.openZones().subscribe({
      next: step => {
        if (step) {
          this.flow.go(step);
        } else {
          this.snackBar.open('Nessuna zona disponibile per codificare il cliente', 'Chiudi', { duration: 5000 });
        }
      },
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, 'Chiudi', { duration: 5000 });
        }
      },
    });
  }
}
